import { NavigationGuardReturn, RouteLocationNormalized } from 'vue-router';
import MedicareConsent from '@/consent/medicare/infrastructure/views/MedicareConsent.vue';
import { MedicareSettingsUseCase } from '@/consent/medicare/application/MedicareSettingsUseCase';
import { MedicareSettingsMemory } from '@/consent/medicare/infrastructure/dataSource/MedicareSettingsMemory';
import { checkApplicationSanity } from '@/application/shared/infrastructure/routeGuards';
import SaveAndExitSuccess from '@/application/shared/infrastructure/views/SaveAndExitSuccess.vue';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { CarrierList } from '@hfc/ui-core';
import MedicareContinue from '@/consent/medicare/infrastructure/views/MedicareContinue.vue';

const medicareSettingsRepository = new MedicareSettingsMemory();
const medicareSettings = new MedicareSettingsUseCase(medicareSettingsRepository);

const agentCollection = await medicareSettings.run();

const validateAgentEnabled = (agentName: string, originalPath: string): boolean => {
  if (agentName && !agentCollection.isAgentEnabled(agentName)) {
    // Agent doesn't exist in our configuration, redirect to generic consent
    window.location.href = originalPath.replace(`/${agentName}`, '');

    return false;
  }

  return true;
};

const medicareConsentBeforeEnter = (to: RouteLocationNormalized): NavigationGuardReturn => {
  const agentName = to.params.agent as string;
  if (!validateAgentEnabled(agentName, to.path)) {
    return false;
  }

  return true;
};

const medicareConsentAepBeforeEnter = (to: RouteLocationNormalized): NavigationGuardReturn => {
  const agentName = to.params.agent as string;
  if (!validateAgentEnabled(agentName, to.path)) {
    return false;
  }

  const agent = agentCollection.getAgent(agentName);
  if (!agent.aepLink) {
    window.location.href = '/not-found';

    return false;
  }

  return true;
};

export const medicareConsentRouter = {
  path: '/medicare',
  name: 'Medicare',
  redirect: {
    name: 'MedicareAppointment',
  },
  children: [{
    path: 'appointment/aep/:agent',
    name: 'MedicareAppointmentAep',
    beforeEnter: medicareConsentAepBeforeEnter,
    component: MedicareConsent,
    props: {
      appointment: true,
      appointmentKey: 'aepLink'
    }
  }, {
    path: 'appointment/:agent?',
    name: 'MedicareAppointment',
    beforeEnter: medicareConsentBeforeEnter,
    component: MedicareConsent,
    props: {
      appointment: true
    }
  }, {
    path: 'consent/:agent?',
    name: 'MedicareConsent',
    component: MedicareConsent,
    beforeEnter: medicareConsentBeforeEnter,
    props: {
      appointment: false
    }
  }, {
    path: ':type(consent|appointment)/save-and-exit/:uuid',
    name: 'MedicareSaveAndExit',
    component: SaveAndExitSuccess,
    beforeEnter: (to: RouteLocationNormalized, _from: RouteLocationNormalized): NavigationGuardReturn => checkApplicationSanity('medicareSaveAndExit', to.params.type === 'consent' ? 'MedicareConsent' : 'MedicareAppointment'),
    props: (route: RouteLocationNormalized): Record<string, unknown> => ({
      applicationType: APPLICATION_TYPES.MEDICARE,
      carrier: CarrierList.HFC,
      routeName: 'MedicareContinue',
      routeParam: 'uuid',
      linkText: route.params.type === 'appointment'
        ? 'Resume Disclosure and Appointment Scheduler Now'
        : 'Resume Disclosure Now',
      message: `
      <p>Your information has been saved, and we've sent you an email and sms with directions on how to resume your Medicare Consent.</p>
      <p>Please check your email and sms for the link to continue your consent.</p>`
    }),
  }, {
    path: '/medicare/:uuid/continue-applying',
    name: 'MedicareContinue',
    component: MedicareContinue
  }],
};
